
import { defineComponent, onMounted, computed } from 'vue';
import SPage from 'PortalDir/components/page/SPage.vue';
import { preFetch } from 'quasar/wrappers';
import { PublicNewsApi } from 'SharedDir/backend/api';
import { useDeviceSize } from 'SharedDir/common/composables/useDeviceSize';
import { useLoadingSsr } from 'PortalDir/common/composables/useLoadingSsr';
import { toRussianLocaleDateFromIso, isErrorHasCode, random } from '@packages/toolbox';
import { getErrorCode } from '@packages/toolbox/dist/utils/http-handle';
import { AxiosError } from 'axios';
import { HTTP_CODE_ERROR } from '@packages/toolbox/dist/constants/http-codes';
import SPageHeader from 'components/page/SPageHeader.vue';
import { useNewsStore } from 'stores/news';
import { NewsBare } from 'SharedDir/declarations/news';
import { useImageFallback } from 'SharedDir/common/composables/useImageFallback';
import NewsSkeletonItem from 'PortalDir/modules/News/components/SkeletonItem.vue';
import { useI18n } from 'vue-i18n';
import { useMetaPortal } from 'SharedDir/common/composables/useMetaPortal';
import { useMeta } from 'quasar';
import { cropTextSmart, removeHtml } from 'SharedDir/utils/helpers';

async function getData(newsStore, newsId) {
  const data = await PublicNewsApi.get(newsId);
  newsStore.setNews(data);
}

export default defineComponent({
  name: 'NewsView',
  methods: { random },
  components: {
    NewsSkeletonItem,
    SPageHeader,
    SPage,
  },
  props: {
    newsId: {
      type: String,
      required: true,
    },
  },

  preFetch: preFetch(async ({ store, currentRoute, ssrContext, redirect }) => {
    const newsStore = useNewsStore();
    try {
      await getData(newsStore, currentRoute.params.newsId);
    } catch (err: any) {
      const errorCode = getErrorCode(err as AxiosError);
      if (isErrorHasCode(errorCode, HTTP_CODE_ERROR.NOT_FOUND)) {
        redirect({
          path: '/404',
        });
      }
    }
  }),

  setup(props) {
    const newsStore = useNewsStore();

    const { isLoading, showLoading } = useLoadingSsr();
    const { t } = useI18n();

    const news = computed(() => {
      return newsStore.news as NewsBare;
    });

    const pageTitle = computed(() => {
      return news.value?.title || '';
    });

    const getNewsDate = computed(() => {
      return news.value?.publishedAt ? toRussianLocaleDateFromIso(news.value?.publishedAt) : '';
    });

    const getNewsDescription = computed(() => {
      return news.value?.description ?? '';
    });

    const getNewsSource = computed(() => {
      return news.value?.source ?? '';
    });

    const getNewsSourceLink = computed(() => {
      return news.value?.sourceLink ?? '';
    });

    const { getImage, imageNotLoading } = useImageFallback();

    onMounted(async () => {
      isLoading.value = false;
    });

    const { clearFullPageUrl } = useMetaPortal();

    useMeta(() => {
      return {
        title: `${t('systemType')} ${t('systemShortName')} - ${pageTitle.value}`.slice(0, 160),

        meta: {
          description: {
            name: 'description',
            template: () =>
              cropTextSmart(
                removeHtml(
                  getNewsDescription.value ? getNewsDescription.value : t('systemDescription')
                ),
                152
              ),
          },
          keywords: {
            name: 'keywords',
            template: () =>
              [
                `${pageTitle.value}`.replaceAll('"', ''),
                `${t('systemType')} ${t('systemShortName')}`,
                `Новости ${t('systemShortName')}`,
              ].join(', '),
          },
        },
        link: {
          canonical: {
            rel: 'canonical',
            href: clearFullPageUrl.value,
          },
        },
      };
    });

    return {
      news,
      pageTitle,
      getNewsDate,
      ...useDeviceSize(),
      isLoading,
      showLoading,
      getImage,
      imageNotLoading,
      getNewsDescription,
      getNewsSource,
      getNewsSourceLink,
    };
  },
});
